import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookieConsent, COOKIE_PREFERENCES_OPEN_EVENT } from '@/context/CookieConsentContext.jsx';
import { trackTelemetry } from '@/lib/telemetryClient.js';
import { PUBLIC_TELEMETRY_EVENTS } from '@/lib/publicTelemetryContract.js';

function useCookieConsentUi() {
    const cookieConsent = useCookieConsent();
    const { updatedAt, categories } = cookieConsent;
    const [isCookiePreferencesOpen, setIsCookiePreferencesOpen] = useState(false);
    const [isCookieBannerVisible, setIsCookieBannerVisible] = useState(() => !updatedAt);
    const previousConsentUpdatedAtRef = useRef(updatedAt);

    useEffect(() => {
        const previousUpdatedAt = previousConsentUpdatedAtRef.current;
        const consentJustUpdated = Boolean(updatedAt) && updatedAt !== previousUpdatedAt;

        if (updatedAt) {
            setIsCookieBannerVisible(false);
            if (consentJustUpdated) {
                setIsCookiePreferencesOpen(false);
            }
        } else if (!isCookiePreferencesOpen) {
            setIsCookieBannerVisible(true);
        }

        previousConsentUpdatedAtRef.current = updatedAt || null;
    }, [updatedAt, isCookiePreferencesOpen]);

    const handleOpenCookiePreferences = useCallback(() => {
        setIsCookiePreferencesOpen(true);
        setIsCookieBannerVisible(false);
        trackTelemetry(PUBLIC_TELEMETRY_EVENTS.COOKIE_PREFERENCES_OPENED, {
            trigger: isCookieBannerVisible ? 'banner' : 'layout_menu',
        });
    }, [isCookieBannerVisible]);

    const handleCloseCookiePreferences = useCallback(() => {
        setIsCookiePreferencesOpen(false);
        if (!updatedAt) {
            setIsCookieBannerVisible(true);
        }
        trackTelemetry(PUBLIC_TELEMETRY_EVENTS.COOKIE_PREFERENCES_CLOSED, {
            has_existing_consent: Boolean(updatedAt),
        });
    }, [updatedAt]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleExternalRequest = () => {
            handleOpenCookiePreferences();
        };

        window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleExternalRequest);
        return () => {
            window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, handleExternalRequest);
        };
    }, [handleOpenCookiePreferences]);

    return {
        isCookieBannerVisible,
        isCookiePreferencesOpen,
        handleOpenCookiePreferences,
        handleCloseCookiePreferences,
        consentCategories: categories,
        cookieConsentUpdatedAt: updatedAt,
    };
}

export default useCookieConsentUi;
